<template>
  <q-header>
    <q-toolbar class="tw-header">
      <!-- Zone gauche : navigation, logo et selecteur d'affaire -->
      <div class="tw-header-logo">
        <q-btn data-testid="menu_button" class="tw-nav-trigger" id="icon" flat icon="menu" @click="collapseToggle" />
        <img src="../../assets/images/Logo-coloré.png" alt="teamWill Digital" class="tw-header-logo-img" />

        <!-- Le selecteur d'affaire vit desormais en bas du menu lateral -->
        <q-btn-dropdown class="tw-header-selector" v-if="delegations.length" :label="selectUserDelegate.label"
          flat no-caps dropdown-icon="expand_more">
          <q-list>
            <q-item v-for="item in delegations" :key="item.value" clickable v-close-popup @click="onSelectUser(item)">
              <q-item-section>
                <q-item-label>{{ $t(`main.delegation.${item.label}`) }}</q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
        </q-btn-dropdown>
      </div>

      <!-- Zone centrale : recherche rapide, affichee en permanence -->
      <div class="tw-header-search">
        <QuickSearch always-open />
      </div>

      <!-- Zone droite : alertes, langue, identite -->
      <div class="tw-header-nav">
        <button type="button" class="tw-header-icon-btn" id="bellBtn" @click="showMessages = true">
          <q-icon name="notifications_none" size="20px" />
          <span class="tw-header-badge" v-if="unreadCount > 0">{{ unreadCount }}</span>
          <q-tooltip>{{ messagesTooltip }}</q-tooltip>
        </button>

        <!-- Bascule de langue, sortie du panneau de profil pour un acces direct -->
        <div class="tw-header-lang" v-if="languageToggles.length > 1">
          <button v-for="lang in languageToggles" :key="lang.id" type="button" class="tw-header-lang-btn"
            :class="{ 'is-active': lang.active }" @click="onSelectLanguage(lang.option)">
            {{ lang.code }}
          </button>
        </div>

        <q-btn-dropdown content-class="test" class="tw-header-user" flat no-caps no-icon-animation dropdown-icon=""
          floating>
          <template #label>
            <span class="tw-header-user-identity">
              <span class="tw-header-user-name">{{ userFullName }}</span>
              <span class="tw-header-user-role">{{ userRole }}</span>
            </span>
            <q-avatar size="34px" color="amber" text-color="white">
              {{ initials }}
            </q-avatar>
          </template>
          <template #dropdown-arrow></template>
          <LanguageProfil />
        </q-btn-dropdown>

        <button type="button" data-testid="logout_btn" class="tw-header-icon-btn" @click="logout">
          <q-icon name="logout" size="19px" />
          <q-tooltip>{{ logoutTooltip }}</q-tooltip>
        </button>
      </div>
    </q-toolbar>
  </q-header>

  <!-- ── Messages Panel (global, no order context) ── -->
  <OrderMessagesPanel v-model="showMessages" v-model:unreadCount="unreadCount" :subjects-list="subjectsList"
    :reference-options="optionsReference" @message-sent="onMessageSent" :alertList="$store.state.alertList" />

  <q-dialog v-model="confirmLogout" persistent>
    <q-card>
      <q-card-section class="row items-center">
        <div class="col-1">
          <q-icon name="info" color="primary" text-color="white" size="2rem" />
        </div>
        <div class="col-11">
          <span class="q-ml-sm">{{ $t("main.confirmationDialog.message") }}</span>
        </div>
      </q-card-section>
      <q-card-actions align="right">
        <q-btn flat :label="confirmationButtonLabel" color="primary" v-close-popup @click="onClickLogoutButton" />
        <q-btn flat :label="cancelButtonLabel" color="primary" v-close-popup />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import { Emit } from "vue-property-decorator";
import { ref } from "vue";
import store from "@/store";
import i18n from "@/i18n";
import Notification from "@/components/communication/Notification.vue";
import LanguageProfil from "@/components/user-preference/LanguageProfile.vue";
import QuickSearch from "@/components/layout/QuickSearch.vue";
import OrderMessagesPanel from "@/components/communication/OrderMessagesPanel.vue";
import { mapActions, mapGetters, mapState } from "vuex";
import { Message } from "@/types";
import { settings } from "@/settings";
import { globalConfig, SESSION_TOKEN_KEY } from "@/utils";
import { getConnectedUserRoleLabel } from "@/utils/user-role";
import { getTranslations } from "@/i18n.service.handler";
import { fetchEventSource } from "@microsoft/fetch-event-source";

const systemUid = `odm-contactmechanism`;
const baseUrlGenerated = `${settings.api_url}/${systemUid}/api/1/event/${systemUid}`;

@Options({
  name: "app-header",
  components: {
    Notification,
    LanguageProfil,
    QuickSearch,
    OrderMessagesPanel,
  },
  computed: {
    ...mapState("notificationModule", ["notifications"]),
    ...mapGetters("notificationModule", ["getData", "getDataSize"]),
    ...mapState("messagingModule", ["recipients", "subjects"]),
    ...mapGetters("messagingModule", ["getRecipients"]),
  },
  methods: {
    ...mapActions("notificationModule", ["fetchData"]),
    ...mapActions("messagingModule", ["fetchRecipients", "fetchSubjects"]),
    onClickNotif() {
      this.fetchRecipients();
    },
  },
  watch: {
    // Le libelle d'affaire est porte par le menu lateral ; ici on ne garde que
    // le rafraichissement des notifications, qui dependent de l'affaire active.
    "$store.state.authModule.selectedUserNetwork"() {
      this.fetchData(null);
    },
  },
})
export default class Header extends Vue {
  message!: Message;
  filterInput = "";
  seeAllBtn = false;
  unreadBtn = false;
  readBtn = false;
  tempData: any;
  getData!: () => Message[];
  getAllUser!: () => void;
  fetchData!: Function;
  fetchRecipients!: () => void;
  fetchSubjects!: () => void;
  getDataSize!: () => number;
  showDetails = false;
  userDelegationLabel: any = "";

  // ── Messages panel state ──
  showMessages: boolean = false;
  unreadCount = ref(0);
  optionsRefrence: any[] = [];

  show() {
    this.showDetails = true;
  }

  get listener() {
    // Server-Sent Events listener for real-time notifications.
    // We dispatch directly to the store to avoid `this` context pitfalls
    // inside fetchEventSource callbacks.
    return async () => {
      await fetchEventSource(`${baseUrlGenerated}/listener/`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem(SESSION_TOKEN_KEY)}`,
        },
        async onopen(res) {
          if (res.ok && res.status === 200) {
            console.warn("Connection made ", res);
          } else if (res.status >= 400 && res.status < 500 && res.status !== 429) {
            console.warn("Client side error ", res);
          }
        },
        onmessage: (event) => {
          const rawData = (event?.data || "").trim();
          // Keep-alive frames should not trigger a fetch.
          if (!rawData || rawData === "*|*") return true;
          // Backend can emit quoted payloads; normalize before fetch.
          const eventUid = rawData.replace(/^"+|"+$/g, "");
          if (!eventUid) return true;
          // Use store dispatch directly from SSE callback (more reliable than component-bound action context).
          store.dispatch("notificationModule/fetchData", { resourceUid: eventUid });
          return true;
        },
        onclose() {
          console.warn("Connection closed by the server");
        },
        onerror(err) {
          console.warn("There was an error from server", err);
        },
      });
    };
  }

  created() {
    this.listener();
    this.loadData();
    this.fetchRecipients();
    this.fetchSubjects();
    this.getDataSize;
    store.dispatch("setAlertList");

    // Load reference options for the messages panel
    store.dispatch("demandModule/searchOfferPartyOrder", {
      callback: (res: any) => { this.optionsRefrence = res; },
      offerFilter: {},
      filterParty: {},
      orderFilter: {},
    });
  }

  loadData() {
    this.fetchData(null);
  }

  confirmLogout = false;

  get daaqModel() {
    const userDaaq = store?.state?.authModule?.daaqResourceUid;
    if (userDaaq) {
      return { value: userDaaq, label: store?.state?.authModule?.daaqLabel };
    }
    return undefined;
  }

  get selectUserDelegate() {
    return store?.state?.authModule?.selectedUserDelegation;
  }

  async onSelectUser(user: any) {
    if (user && user.label) {
      store.dispatch("authModule/setSelectedUserDelegation", { user });
      this.userDelegationLabel = i18n.global.t(`main.userDelegation.${user.label}`);
      this.fetchData(null);
      store.dispatch("authModule/changeUserDelegation", user);
    }
  }

  onSelectDAAQ(event: any) {
    store.dispatch("authModule/selectDAAQ", {
      daaq: event.config.daaq,
      daaqResourceUid: event.config.resourceUid,
      daaqLabel: event.config.label,
    });
  }

  @Emit("collapseToggle")
  collapseToggle() { }

  logout() {
    this.confirmLogout = true;
  }

  get daaqList() {
    return this.user?.daaqList || [];
  }

  get daaqLabel() {
    return this.daaqModel?.label ? i18n.global.t(`main.daaq.${this.daaqModel.label}`) : "";
  }

  get user() {
    return store?.state?.authModule?.user;
  }

  get initials() {
    const firstInitial = this.user?.firstName?.trim()?.charAt(0) || "";
    const lastInitial = this.user?.lastName?.trim()?.charAt(0) || "";
    return `${firstInitial}${lastInitial}`.toUpperCase() || "--";
  }

  get userFullName() {
    return `${this.user?.firstName || ""} ${this.user?.lastName || ""}`.trim();
  }

  /** Libellé de rôle mutualisé avec la carte de profil, cf. utils/user-role.ts */
  get userRole() {
    return getConnectedUserRoleLabel();
  }

  get messagesTooltip() {
    return i18n.global.t("main.header.messages");
  }

  get logoutTooltip() {
    return i18n.global.t("main.header.logout");
  }

  /**
   * Langues proposées dans la bascule du header, dérivées des langues
   * autorisées pour l'utilisateur. Le code affiché est la partie langue de la
   * locale (`fr-FR` → `FR`).
   */
  get languageToggles() {
    const options = store.getters["authModule/getUserConnected"]?.preferredLanguages || [];
    const currentLanguage = i18n.global.locale.value?.split("-")[0];
    return options
      .filter((option: any) => option?.id)
      .map((option: any) => {
        const language = option.id.split("-")[0];
        return {
          id: option.id,
          code: language.toUpperCase(),
          active: language === currentLanguage,
          option,
        };
      });
  }

  onSelectLanguage(option: any) {
    getTranslations(option);
  }

  get delegations() {
    return store?.state?.authModule?.delegations || [];
  }

  get cancelButtonLabel() {
    return i18n.global.t("main.confirmationDialog.cancelButton");
  }

  get confirmationButtonLabel() {
    return i18n.global.t("main.confirmationDialog.confirmationButton");
  }

  get msgStatusTabs() {
    return store?.state?.notificationModule?.msgStatusTabs;
  }

  get selectedstatusTab() {
    return store?.state?.notificationModule?.msgSelectedStatusTab;
  }

  get LogoConfig() {
    let localStorageLogoConfig: any = undefined;
    const configurationLocalStorage = localStorage.getItem(globalConfig.storageKeys.configuration);
    if (configurationLocalStorage) {
      const logoConfig = JSON.parse(configurationLocalStorage)?.logo;
      if (logoConfig && logoConfig.length > 0) {
        localStorageLogoConfig = logoConfig[0]?.fiedld_value;
      }
    }
    const logo =
      store?.state?.authModule?.configuration?.logo?.length > 0
        ? store.state.authModule?.configuration.logo[0].fiedld_value
        : undefined;
    return logo || localStorageLogoConfig;
  }

  get filteredData() {
    this.tempData = this.getData;
    const filtered = this.tempData.filter((item: Notification) => {
      return this.filterInput && this.filterInput !== ""
        ? item.subject.resourceUid.toLowerCase().includes(this.filterInput.toLowerCase()) ||
        item.metadata.find((i: any) => i.key === "Entity_Reference")?.value.includes(this.filterInput) ||
        item.sender.toLowerCase().includes(this.filterInput.toLowerCase())
        : this.getData;
    });
    filtered.sort((a: any, b: any) => b.communicationDate - a.communicationDate);
    return filtered.filter((item: Notification) =>
      this.selectedstatusTab === "ALL" ? filtered : item.status.resourceUid === this.selectedstatusTab
    );
  }

  sortNotificationList(type?: string) {
    this.tempData.sort((a: Notification, b: Notification) => {
      switch (type) {
        case "unread":
          return a.status.resourceUid < b.status.resourceUid ? -1 : a.status.resourceUid > b.status.resourceUid ? 1 : 0;
        case "importance":
          return b.priority - a.priority;
        default:
          return new Date(b.communicationDate).getTime() - new Date(a.communicationDate).getTime();
      }
    });
  }

  onClickLogoutButton() {
    this.$store.dispatch("authModule/signOut");
  }

  onStatusTabChanged(selectedStatus: string) {
    this.$store.dispatch("notificationModule/updateSelectedStatusTab", selectedStatus);
  }

  // ── Messages panel helpers ──
  get subjectsList() {
    return (this as any).subjects?.map((subject: any) => subject.id) || [];
  }

  get optionsReference() {
    return this.optionsRefrence;
  }

  onMessageSent() { }
}
</script>

<!-- Styles du header : centralises dans src/css/_theme.scss -->
